import { escapeRegExp } from 'lodash';

export const APP_ORIGIN = 'https://app.littlebird.ai';
export const APP_HOST = new URL(APP_ORIGIN).host;

export const WORD_CHARACTER = '[\\p{L}\\p{M}\\p{N}_]';
// A mention can't follow a word, a URL path (`youtube.com/@name`), `!` (image syntax) or an escaping backslash
export const MENTION_BLOCKER = '[\\p{L}\\p{M}\\p{N}_/!\\\\]';
// Code, existing links, autolinks and bare URLs are left as written
export const UNTOUCHED =
  '`{3,}[\\s\\S]*?(?:`{3,}|$)|~{3,}[\\s\\S]*?(?:~{3,}|$)|(?<ticks>`+)[\\s\\S]*?\\k<ticks>|\\[[^\\]]*\\]\\([^)]*\\)|<[^>\\s]+>|\\bhttps?:\\/\\/\\S+';
export const MARKDOWN_SPECIAL_CHARACTERS = /[\\`*_[\]()<>~$|&!]/g;

export const escapeMarkdown = (text: string) =>
  text.replace(MARKDOWN_SPECIAL_CHARACTERS, '\\$&');

const APP_PATHNAME = /^\/apps\/([^/]+)$/;

export type ParsedAppMentionUrl =
  | { kind: 'app'; name: string }
  | { kind: 'none' };

export const appMentionUrl = (name: string) =>
  `${APP_ORIGIN}/apps/${encodeURIComponent(name).replace(
    /[!'()*]/g,
    (character) => `%${character.charCodeAt(0).toString(16).toUpperCase()}`
  )}`;

export function parseAppMentionUrl(href: string): ParsedAppMentionUrl {
  try {
    const url = new URL(href, APP_ORIGIN);
    if (url.protocol !== 'https:' || url.host !== APP_HOST) {
      return { kind: 'none' };
    }
    const app = url.pathname.match(APP_PATHNAME);
    return app
      ? { kind: 'app', name: decodeURIComponent(app[1]) }
      : { kind: 'none' };
  } catch {
    return { kind: 'none' };
  }
}

export const createAppMentionLinker = (names: readonly string[]) => {
  const urlsByName = new Map<string, string>();
  for (const name of names) {
    if (name && !urlsByName.has(name)) {
      urlsByName.set(name, appMentionUrl(name));
    }
  }
  if (urlsByName.size === 0) return (text: string) => text;

  const escapedNames = [...urlsByName.keys()]
    .sort((a, b) => b.length - a.length)
    .map(escapeRegExp);
  const mention = `(?<!${MENTION_BLOCKER})@(?:${escapedNames.join('|')})(?!${WORD_CHARACTER})`;
  const pattern = new RegExp(`${UNTOUCHED}|${mention}`, 'gu');

  return (text: string) =>
    text.replace(pattern, (match) => {
      if (!match.startsWith('@')) return match;
      const url = urlsByName.get(match.slice(1));
      return url ? `[${escapeMarkdown(match)}](${url})` : match;
    });
};
